import { supabase } from '@/integrations/supabase/client';

export interface CmsPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  read_time: string;
  excerpt: string;
  body_markdown: string;
  cover_image_url: string | null;
  seo_title: string | null;
  meta_description: string | null;
  published_at: string | null;
  is_featured: boolean;
  author_name: string | null;
}

export async function fetchLivePosts(): Promise<CmsPost[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id,slug,title,category,read_time,excerpt,body_markdown,cover_image_url,seo_title,meta_description,published_at,is_featured,author_name,status')
    .eq('status', 'published')
    .or(`published_at.is.null,published_at.lte.${new Date().toISOString()}`)
    .order('published_at', { ascending: false, nullsFirst: false });
  if (error) {
    console.error('Failed to fetch CMS posts', error);
    return [];
  }
  return (data || []) as CmsPost[];
}

export async function fetchPostBySlug(slug: string): Promise<CmsPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id,slug,title,category,read_time,excerpt,body_markdown,cover_image_url,seo_title,meta_description,published_at,is_featured,author_name,status')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();
  if (error || !data) return null;
  if (data.published_at && new Date(data.published_at) > new Date()) return null;
  return data as CmsPost;
}

/** Tiny markdown → HTML for blog body. Pair with sanitizeHtml() before rendering. */
export function renderMarkdown(md: string): string {
  if (!md) return '';
  const escape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let para: string[] = [];
  let inUl = false;
  let inOl = false;

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inlineMd(para.join(' '))}</p>`);
      para = [];
    }
  };
  const closeLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };

  const inlineMd = (s: string) =>
    escape(s)
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" loading="lazy" class="rounded-md my-4 max-w-full h-auto" />')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" rel="noreferrer">$1</a>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); closeLists(); continue; }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      flushPara(); closeLists();
      const lvl = h[1].length;
      out.push(`<h${lvl}>${inlineMd(h[2])}</h${lvl}>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara();
      if (inOl) { out.push('</ol>'); inOl = false; }
      if (!inUl) { out.push('<ul>'); inUl = true; }
      out.push(`<li>${inlineMd(line.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      flushPara();
      if (inUl) { out.push('</ul>'); inUl = false; }
      if (!inOl) { out.push('<ol>'); inOl = true; }
      out.push(`<li>${inlineMd(line.replace(/^\d+\.\s+/, ''))}</li>`);
      continue;
    }
    para.push(line);
  }
  flushPara(); closeLists();
  return out.join('\n');
}
